import { useTranslation } from 'react-i18next';
import { useStrava } from '../hooks/useStrava.ts';

const STRAVA_ORANGE = '#FC4C02';

function formatRelative(iso: string | null | undefined, t: (k: string) => string): string {
  if (!iso) return t('strava.never');
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return t('strava.justNow');
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} ${t('strava.minutesAgo')}`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} ${t('strava.hoursAgo')}`;
  return `${Math.round(diffSec / 86400)} ${t('strava.daysAgo')}`;
}

export function StravaConnect() {
  const { t } = useTranslation();
  const { status, connect, sync, disconnect } = useStrava();

  if (status.isLoading) {
    return (
      <div className="rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4">
        <p className="text-sm text-(--color-text-secondary)">{t('strava.loading')}</p>
      </div>
    );
  }

  const data = status.data;
  if (!data?.configured) {
    return (
      <div className="rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4">
        <h3 className="text-sm font-semibold text-(--color-text-primary) mb-1">Strava</h3>
        <p className="text-xs text-(--color-text-secondary)">{t('strava.notConfigured')}</p>
      </div>
    );
  }

  if (!data.connected) {
    return (
      <div className="rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-(--color-text-primary)">Strava</h3>
          <span className="text-xs text-(--color-text-secondary)">
            {t('strava.notConnected')}
          </span>
        </div>
        <p className="text-xs text-(--color-text-secondary)">
          {t('strava.connectBenefit')}
        </p>
        <button
          onClick={() => connect.mutate()}
          disabled={connect.isPending}
          className="self-start px-4 py-2 rounded-lg text-sm font-bold text-white shadow-md hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          style={{ backgroundColor: STRAVA_ORANGE }}
        >
          {connect.isPending ? `${t('strava.connecting')}…` : t('strava.connect')}
        </button>
      </div>
    );
  }

  const job = data.last_job;
  const isSyncing = job?.status === 'running' || sync.isPending;
  const lastSync = job?.finished_at ?? job?.started_at;

  let syncStatusText: string;
  if (isSyncing) {
    syncStatusText = `${t('strava.syncing')}… ${job?.activities_fetched ?? 0} ${t('strava.rides')}`;
  } else if (job?.status === 'failed') {
    syncStatusText = t('strava.syncFailed');
  } else if (job) {
    syncStatusText = `${t('strava.lastSync')}: ${formatRelative(lastSync, t)}`;
  } else {
    syncStatusText = t('strava.neverSynced');
  }

  return (
    <div className="rounded-lg border border-(--color-border) bg-(--color-bg-tertiary) p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-(--color-text-primary)">Strava</h3>
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ backgroundColor: `${STRAVA_ORANGE}20`, color: STRAVA_ORANGE }}
        >
          {t('strava.connected')}
        </span>
      </div>
      <div className="text-xs text-(--color-text-secondary) flex flex-col gap-1">
        <span>
          {t('strava.athleteId')}: <span className="font-mono">{data.athlete_id}</span>
        </span>
        <span>{syncStatusText}</span>
        {job?.status === 'done' && (
          <span>
            {job.activities_fetched} {t('strava.rides')} · {job.activities_inserted}{' '}
            {t('strava.new')} · {job.activities_updated} {t('strava.updated')}
          </span>
        )}
        {job?.status === 'failed' && job.error_text && (
          <span className="text-red-400 break-all">{job.error_text}</span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => sync.mutate()}
          disabled={isSyncing}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-(--color-accent)/20 text-(--color-accent) border border-(--color-accent) hover:bg-(--color-accent)/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSyncing ? `${t('strava.syncing')}…` : t('strava.syncNow')}
        </button>
        <button
          onClick={() => {
            if (globalThis.confirm(t('strava.disconnectConfirm'))) {
              disconnect.mutate();
            }
          }}
          disabled={disconnect.isPending}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-(--color-bg-secondary) text-(--color-text-secondary) border border-(--color-border) hover:border-red-400 hover:text-red-400 active:scale-[0.98] disabled:opacity-50 transition-all"
        >
          {disconnect.isPending ? `${t('strava.disconnecting')}…` : t('strava.disconnect')}
        </button>
      </div>
    </div>
  );
}
